import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MessageSquare, Check, X, RefreshCw, Trash2, Link as LinkIcon, AlertCircle, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatRelative = (iso) => {
  if (!iso) return 'never';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const formatSeconds = (s) => {
  if (!s) return '0m';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
};

export default function IntercomPanel() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [token, setToken] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [showToken, setShowToken] = useState(false);

  const [admins, setAdmins] = useState([]);
  const [users, setUsers] = useState([]);
  const [savingMapping, setSavingMapping] = useState({});

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const loadConfig = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/intercom/config`, { headers: authHeaders() });
      setConfig(res.data);
      if (res.data.configured) {
        setWorkspaceId(res.data.workspace_id || '');
      }
    } catch (e) {
      console.error('Failed to load Intercom config:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadAdminsAndMappings = async () => {
    try {
      const [adminsRes, mappingRes] = await Promise.all([
        axios.get(`${API}/intercom/admins`, { headers: authHeaders() }),
        axios.get(`${API}/intercom/admin-mapping`, { headers: authHeaders() }),
      ]);
      setAdmins(adminsRes.data.admins || []);
      setUsers(mappingRes.data.users || []);
    } catch (e) {
      if (e.response?.status === 404) {
        // Not configured — silently skip
        setAdmins([]);
        setUsers([]);
      } else {
        toast.error(`Failed to load admins: ${e.response?.data?.detail || e.message}`);
      }
    }
  };

  useEffect(() => { loadConfig(); }, []);
  useEffect(() => {
    if (config?.configured && config?.enabled) {
      loadAdminsAndMappings();
    }
    // eslint-disable-next-line
  }, [config?.configured, config?.enabled]);

  const saveConfig = async () => {
    if (!token || token.length < 20) {
      toast.error('Please paste a valid Intercom Access Token.');
      return;
    }
    if (!workspaceId) {
      toast.error('Workspace ID is required.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/intercom/config`, {
        access_token: token, workspace_id: workspaceId, enabled: true,
      }, { headers: authHeaders() });
      toast.success(`Connected to "${res.data.workspace?.name || 'Intercom workspace'}".`);
      setToken('');
      setShowForm(false);
      await loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save Intercom config.');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/intercom/test-connection`, {}, { headers: authHeaders() });
      toast.success(`Connected: ${res.data.workspace_name} · ${res.data.admin_count} admins`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection failed.');
    } finally {
      setTesting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Intercom? Your stored token will be removed and syncing will stop.')) return;
    try {
      await axios.delete(`${API}/intercom/config`, { headers: authHeaders() });
      toast.success('Intercom disconnected.');
      setConfig({ configured: false, enabled: false });
      setAdmins([]);
      setUsers([]);
    } catch (e) {
      toast.error('Failed to disconnect.');
    }
  };

  const triggerSync = async (daysBack = 1) => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/intercom/sync-now?days_back=${daysBack}`, {}, { headers: authHeaders() });
      if (res.data.skipped) {
        toast.warning(`Sync skipped: ${res.data.skipped}`);
      } else {
        toast.success(`Synced ${res.data.processed_conversations} conversations across ${res.data.admins_touched} admins.`);
      }
      await loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  const saveUserMapping = async (userId, adminId) => {
    setSavingMapping((s) => ({ ...s, [userId]: true }));
    try {
      await axios.post(`${API}/intercom/admin-mapping`, {
        user_id: userId,
        intercom_admin_id: adminId === '__none__' ? null : adminId,
      }, { headers: authHeaders() });
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, intercom_admin_id: adminId === '__none__' ? null : adminId } : u));
      toast.success('Mapping updated.');
    } catch (e) {
      toast.error('Could not save mapping.');
    } finally {
      setSavingMapping((s) => ({ ...s, [userId]: false }));
    }
  };

  if (loading) return <div className="p-8 text-gray-500">Loading Intercom config...</div>;

  return (
    <div className="space-y-6">
      {/* Connection Status */}
      <Card data-testid="intercom-connection-card">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Intercom Connection
            </span>
            {config?.configured ? (
              <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200">
                <Check className="h-3 w-3 mr-1" /> Connected
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-200">Not connected</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!config?.configured && !showForm && (
            <div className="text-sm text-gray-600 space-y-3">
              <p>Connect your Intercom workspace to pull per-agent utilization metrics (conversations handled, active time, first response, replies) and compare them against scheduled hours.</p>
              <ol className="list-decimal pl-5 space-y-1 text-xs text-gray-500">
                <li>In Intercom, go to <strong>Developer Hub → Your Apps</strong>, open your app, and copy the <strong>Access Token</strong>.</li>
                <li>Note your <strong>Workspace ID</strong> from Intercom Settings → Workspace → General.</li>
                <li>Paste both below. The token is encrypted before storage.</li>
              </ol>
              <Button onClick={() => setShowForm(true)} data-testid="intercom-connect-btn">
                <LinkIcon className="h-4 w-4 mr-2" /> Connect Intercom
              </Button>
            </div>
          )}

          {(showForm || (config?.configured && showForm)) && (
            <div className="space-y-3 p-4 rounded-lg bg-blue-50/40 border border-blue-100" data-testid="intercom-form">
              <div>
                <Label htmlFor="ic-token">Access Token</Label>
                <div className="relative mt-1">
                  <Input
                    id="ic-token"
                    type={showToken ? 'text' : 'password'}
                    placeholder="dG9rOi8vLi4u (Personal Access Token from Intercom)"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    autoComplete="off"
                    data-testid="intercom-token-input"
                  />
                  <button type="button" onClick={() => setShowToken(!showToken)} className="absolute right-2 top-2 text-gray-400 hover:text-gray-700">
                    {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-[11px] text-gray-500 mt-1">Encrypted at rest. Never displayed again after saving.</p>
              </div>
              <div>
                <Label htmlFor="ic-ws">Workspace ID</Label>
                <Input
                  id="ic-ws"
                  className="mt-1"
                  placeholder="e.g. abcd1234"
                  value={workspaceId}
                  onChange={(e) => setWorkspaceId(e.target.value)}
                  data-testid="intercom-workspace-input"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setShowForm(false); setToken(''); }}>Cancel</Button>
                <Button onClick={saveConfig} disabled={saving} data-testid="intercom-save-btn">
                  {saving ? 'Saving...' : 'Save & Test'}
                </Button>
              </div>
            </div>
          )}

          {config?.configured && !showForm && (
            <div className="space-y-3" data-testid="intercom-connected-panel">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                <div className="rounded-md bg-gray-50 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Workspace ID</div>
                  <div className="font-mono text-xs text-gray-900 truncate">{config.workspace_id}</div>
                </div>
                <div className="rounded-md bg-gray-50 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Connected</div>
                  <div className="text-xs text-gray-900">{formatRelative(config.updated_at)}</div>
                </div>
                <div className="rounded-md bg-gray-50 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Last Sync</div>
                  <div className="text-xs text-gray-900">{formatRelative(config.last_sync_at)}</div>
                </div>
                <div className="rounded-md bg-gray-50 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Last Synced</div>
                  <div className="text-xs text-gray-900">{config.last_sync_processed ?? 0} convos</div>
                </div>
              </div>

              {config.last_error && (
                <div className="flex items-start gap-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span>Last sync had an error: <strong>{config.last_error}</strong></span>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={testConnection} disabled={testing} data-testid="intercom-test-btn">
                  {testing ? 'Testing...' : 'Test Connection'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => triggerSync(1)} disabled={syncing} data-testid="intercom-sync-1d-btn">
                  <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                  Sync Last 24h
                </Button>
                <Button variant="outline" size="sm" onClick={() => triggerSync(90)} disabled={syncing} data-testid="intercom-sync-90d-btn">
                  <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                  Backfill 90 Days
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setShowForm(true); setToken(''); }} data-testid="intercom-update-token-btn">
                  Update Token
                </Button>
                <Button variant="outline" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={disconnect} data-testid="intercom-disconnect-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> Disconnect
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User → Intercom Admin Mapping */}
      {config?.configured && config?.enabled && (
        <Card data-testid="intercom-mapping-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-blue-600" />
              Map Employees to Intercom Admins
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1">Match each Shiftela employee to their Intercom agent so we know whose utilization to attribute conversations to.</p>
          </CardHeader>
          <CardContent>
            {admins.length === 0 ? (
              <div className="text-sm text-gray-500 py-4">No Intercom admins returned — verify your token has the right scopes.</div>
            ) : users.length === 0 ? (
              <div className="text-sm text-gray-500 py-4">No active users to map.</div>
            ) : (
              <div className="space-y-2">
                {users.filter((u) => u.has_schedule !== false).map((u) => (
                  <div key={u.id} className="flex items-center gap-3 p-2 rounded-md border border-gray-100 hover:bg-gray-50" data-testid={`mapping-row-${u.id}`}>
                    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm">
                      {(u.name || u.username || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900 truncate">{u.name || u.username}</div>
                      <div className="text-xs text-gray-500 truncate">{u.email} · {u.role}</div>
                    </div>
                    <div className="w-72">
                      <Select
                        value={u.intercom_admin_id || '__none__'}
                        onValueChange={(v) => saveUserMapping(u.id, v)}
                        disabled={!!savingMapping[u.id]}
                      >
                        <SelectTrigger className="h-9" data-testid={`mapping-select-${u.id}`}>
                          <SelectValue placeholder="Select Intercom admin" />
                        </SelectTrigger>
                        <SelectContent className="max-h-80">
                          <SelectItem value="__none__">— No mapping —</SelectItem>
                          {admins.map((a) => (
                            <SelectItem key={a.id} value={a.id}>
                              {a.name} {a.email && <span className="text-xs text-gray-400 ml-1">{a.email}</span>}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {u.intercom_admin_id && (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                        <Check className="h-3 w-3 mr-1" /> Mapped
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
